import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useGetProjectId, usePutProjectId } from '../../api/project-api/project-api';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { TimingFactor, Methods, SprintFrequency, ProjectDto } from '../../api/model';
import { IconArrowLeft, IconUpload, IconX, IconChevronDown, IconAlertTriangle } from '@tabler/icons-react';
import { getPhaseIdProject } from '../../api/phase-api/phase-api';
import { getDeliverObjectIdPhase, deleteDeliverObjectId } from '../../api/deliver-object-api/deliver-object-api';
import ProjectMembers from '../../components/ProjectMembers/ProjectMembers';
import styles from './ProjectSettingsPage.module.scss';

// Normalize backend date (any format) to YYYY-MM-DD for <input type="date">
const toDateInputValue = (date: any): string => {
  if (!date) return 'Not set';
  const str = String(date);
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
};

// Format a YYYY-MM-DD string for readable display (DD.MM.YYYY)
const formatDateDisplay = (date: string): string => {
  if (!date) return 'Not set';
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : date;
};

const ProjectSettingsPage: React.FC = () => {
  const { t } = useTranslation('project');
  const params = useParams({ strict: false });
  const projectId = params.projectId;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  
  const numericProjectId = Number(projectId);
  const { data: projectData, isLoading } = useGetProjectId(numericProjectId);

  const updateProjectMutation = usePutProjectId({
    mutation: {
      onSuccess: async () => {
        showSuccess(t('projectSettings.feedback.saved'));
        await queryClient.invalidateQueries();
      },
    }
  }, queryClient);

  const [formData, setFormData] = useState({
    name: '',
    startTime: '',
    endTime: '',
    timingFactor: '' as TimingFactor | '',
    method: '' as Methods | '',
    sprintFrequency: '' as SprintFrequency | '',
    firstSprintStartDate: '',
    icon: '',
  });

  const [originalData, setOriginalData] = useState(formData);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (projectData) {
      const data = {
        name: projectData.name || '',
        startTime: toDateInputValue(projectData.startTime),
        endTime: toDateInputValue(projectData.endTime),
        timingFactor: (projectData.timingFactor || '') as TimingFactor | '',
        method: (projectData.method || '') as Methods | '',
        sprintFrequency: (projectData.sprintFrequency || '') as SprintFrequency | '',
        firstSprintStartDate: toDateInputValue(projectData.firstSprintStartDate),
        icon: projectData.icon || '',
      };
      setFormData(data);
      setOriginalData(data);
      setPreviewUrl(projectData.icon || '');
    }
  }, [projectData]);

  useEffect(() => {
    const hasAnyChanges = 
      formData.name !== originalData.name ||
      formData.startTime !== originalData.startTime ||
      formData.endTime !== originalData.endTime ||
      formData.timingFactor !== originalData.timingFactor ||
      formData.method !== originalData.method ||
      formData.sprintFrequency !== originalData.sprintFrequency ||
      formData.firstSprintStartDate !== originalData.firstSprintStartDate ||
      formData.icon !== originalData.icon;
    setHasChanges(hasAnyChanges);
  }, [formData, originalData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const updatedData = { ...prev, [field]: value };
      
      if (field === 'method' && value === Methods.CLASSIC) {
        updatedData.sprintFrequency = '';
        updatedData.firstSprintStartDate = '';
      }
      
      return updatedData;
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      showError(t('projectSettings.feedback.invalidImage'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showError(t('projectSettings.feedback.fileTooLarge'));
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setPreviewUrl(base64String);
      setFormData(prev => ({ ...prev, icon: base64String }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setFormData(prev => ({ ...prev, icon: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getChangesList = () => {
    const changes: Array<{ label: string; oldValue: string; newValue: string }> = [];

    if (formData.name !== originalData.name) {
      changes.push({
        label: t('projectSettings.changes.name'),
        oldValue: originalData.name || t('projectSettings.fields.na'),
        newValue: formData.name || t('projectSettings.fields.na'),
      });
    }

    if (formData.startTime !== originalData.startTime) {
      changes.push({
        label: t('projectSettings.changes.startDate'),
        oldValue: formatDateDisplay(originalData.startTime),
        newValue: formatDateDisplay(formData.startTime),
      });
    }

    if (formData.endTime !== originalData.endTime) {
      changes.push({
        label: t('projectSettings.changes.endDate'),
        oldValue: formatDateDisplay(originalData.endTime),
        newValue: formatDateDisplay(formData.endTime),
      });
    }

    if (formData.timingFactor !== originalData.timingFactor) {
      const getTimingLabel = (value: TimingFactor | '') => {
        if (!value) return t('projectSettings.changes.notSet');
        return Object.keys(TimingFactor).find(key => TimingFactor[key as keyof typeof TimingFactor] === value) || value;
      };
      changes.push({
        label: t('projectSettings.changes.timingFactor'),
        oldValue: getTimingLabel(originalData.timingFactor),
        newValue: getTimingLabel(formData.timingFactor),
      });
    }

    if (formData.method !== originalData.method) {
      changes.push({
        label: t('projectSettings.changes.method'),
        oldValue: originalData.method || t('projectSettings.changes.notSet'),
        newValue: formData.method || t('projectSettings.changes.notSet'),
      });
    }

    if (formData.sprintFrequency !== originalData.sprintFrequency) {
      const getSprintLabel = (value: SprintFrequency | '') => {
        if (!value) return t('projectSettings.changes.notSet');
        switch (value) {
          case SprintFrequency.WEEKLY_1: return '1 Week';
          case SprintFrequency.WEEKLY_2: return '2 Weeks';
          case SprintFrequency.WEEKLY_3: return '3 Weeks';
          case SprintFrequency.MONTHLY: return '1 Month';
          case SprintFrequency.WEEKLY_5: return '5 Weeks';
          default: return value;
        }
      };
      changes.push({
        label: t('projectSettings.changes.sprintDuration'),
        oldValue: getSprintLabel(originalData.sprintFrequency),
        newValue: getSprintLabel(formData.sprintFrequency),
      });
    }

    if (formData.firstSprintStartDate !== originalData.firstSprintStartDate) {
      changes.push({
        label: t('projectSettings.changes.firstSprintDate'),
        oldValue: formatDateDisplay(originalData.firstSprintStartDate),
        newValue: formatDateDisplay(formData.firstSprintStartDate),
      });
    }

    if (formData.icon !== originalData.icon) {
      changes.push({
        label: t('projectSettings.changes.icon'),
        oldValue: originalData.icon ? t('projectSettings.changes.set') : t('projectSettings.changes.notSet'),
        newValue: formData.icon ? t('projectSettings.changes.set') : t('projectSettings.changes.notSet'),
      });
    }

    return changes;
  };

  const handleSaveClick = () => {
    // Validation
    if (!formData.name.trim()) {
      showError(t('projectSettings.feedback.nameRequired'));
      return;
    }

    if (!projectData?.projectType) {
      showError(t('projectSettings.feedback.typeMissing'));
      return;
    }

    if (!projectData?.projectTypeUid) {
      showError(t('projectSettings.feedback.typeUidMissing'));
      return;
    }

    if (!projectData?.complexity) {
      showError(t('projectSettings.feedback.complexityMissing'));
      return;
    }

    // Date validations
    if (!formData.startTime) {
      showError(t('projectSettings.feedback.startRequired'));
      return;
    }

    if (!formData.endTime) {
      showError(t('projectSettings.feedback.endRequired'));
      return;
    }

    if (formData.startTime && formData.endTime) {
      const startDate = new Date(formData.startTime);
      const endDate = new Date(formData.endTime);
      
      if (startDate > endDate) {
        showError(t('projectSettings.feedback.startAfterEnd'));
        return;
      }
    }

    // Validate for Agile and Hybrid
    if (formData.method === Methods.AGILE || formData.method === Methods.HYBRID) {
      if (!formData.sprintFrequency) {
        showError(t('projectSettings.feedback.sprintRequired'));
        return;
      }

      if (!formData.firstSprintStartDate) {
        showError(t('projectSettings.feedback.firstSprintRequired'));
        return;
      }

      // Validate first sprint start date
      if (formData.firstSprintStartDate && formData.startTime) {
        const firstSprintDate = new Date(formData.firstSprintStartDate);
        const projectStartDate = new Date(formData.startTime);
        
        if (firstSprintDate < projectStartDate) {
          showError(t('projectSettings.feedback.firstSprintBeforeStart'));
          return;
        }

        if (formData.endTime) {
          const projectEndDate = new Date(formData.endTime);
          if (firstSprintDate > projectEndDate) {
            showError(t('projectSettings.feedback.firstSprintAfterEnd'));
            return;
          }
        }
      }
    }

    // Validate required fields
    if (!formData.timingFactor) {
      showError(t('projectSettings.feedback.timingRequired'));
      return;
    }

    if (!formData.method) {
      showError(t('projectSettings.feedback.methodRequired'));
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setIsProcessing(true);
    
    try {
      const isMethodChangingToClassic =
        formData.method === Methods.CLASSIC &&
        (originalData.method === Methods.AGILE || originalData.method === Methods.HYBRID);

      if (isMethodChangingToClassic) {
        const phases = await getPhaseIdProject(numericProjectId);
        const agilePhases = phases.filter((phase) => phase.subTypeAgile);

        const deliverablesByPhase = await Promise.all(
          agilePhases
            .filter((phase) => phase.id != null)
            .map((phase) => getDeliverObjectIdPhase(phase.id!))
        );

        const deliverablesToDelete = deliverablesByPhase.flat().filter((d) => d.id != null);

        await Promise.all(
          deliverablesToDelete.map((d) => deleteDeliverObjectId(d.id!))
        );
      }

      const updatedProjectData: ProjectDto = {
        ...projectData,
        name: formData.name,
        method: formData.method || projectData?.method,
        startTime: formData.startTime || undefined,
        endTime: formData.endTime || undefined,
        timingFactor: formData.timingFactor || projectData?.timingFactor,
        icon: formData.icon || undefined,
      };

      if (formData.method === Methods.AGILE || formData.method === Methods.HYBRID) {
        updatedProjectData.sprintFrequency = formData.sprintFrequency || projectData?.sprintFrequency;
        updatedProjectData.firstSprintStartDate = formData.firstSprintStartDate || undefined;
      } else {
        updatedProjectData.sprintFrequency = undefined;
        updatedProjectData.firstSprintStartDate = undefined;
      }

      await updateProjectMutation.mutateAsync({ 
        id: numericProjectId, 
        data: updatedProjectData 
      });
      
      setOriginalData(formData);
      setIsProcessing(false);
      setShowConfirmModal(false);
      navigate({ to: '/project/$projectId', params: { projectId: String(numericProjectId) }, search: { tab: 'overview' } });
    } catch (error) {
      setIsProcessing(false);
      setShowConfirmModal(false);
      showError(t('projectSettings.feedback.saveFailedRetry'));
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setPreviewUrl(originalData.icon);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBack = () => {
    navigate({ to: '/project/$projectId', params: { projectId: projectId || '' }, search: { tab: 'overview' } });
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <p>{t('projectSettings.loading')}</p>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className={styles.error}>
        <p>{t('projectSettings.notFound')}</p>
        <button onClick={() => navigate({ to: '/dashboard' })}>{t('projectSettings.goToDashboard')}</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button onClick={handleBack} className={styles.backButton}>
          <IconArrowLeft size={20} />
          <span>{t('projectSettings.back')}</span>
        </button>
        <h1>{t('projectSettings.title')}</h1>
      </div>

      <div className={styles.content}>
        <section className={styles.section}>
          <h2>{t('projectSettings.editable')}</h2>
          
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="name">{t('projectSettings.fields.name')}</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('projectSettings.fields.namePlaceholder')}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="startTime">{t('projectSettings.fields.startDate')}</label>
              <input
                id="startTime"
                type="date"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="endTime">{t('projectSettings.fields.endDate')}</label>
              <input
                id="endTime"
                type="date"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="timingFactor">{t('projectSettings.fields.timingFactor')}</label>
              <div className={styles.selectWrapper}>
                <select
                  id="timingFactor"
                  value={formData.timingFactor}
                  onChange={(e) => handleInputChange('timingFactor', e.target.value)}
                  className={styles.select}
                >
                  <option value="">{t('projectSettings.fields.timingFactorPlaceholder')}</option>
                  <option value={TimingFactor.FIVE}>5%</option>
                  <option value={TimingFactor.TEN}>10%</option>
                  <option value={TimingFactor.FIFTEEN}>15%</option>
                  <option value={TimingFactor.TWENTY}>20%</option>
                  <option value={TimingFactor.TWENTY_FIVE}>25%</option>
                  <option value={TimingFactor.THIRTY}>30%</option>
                  <option value={TimingFactor.THIRTY_FIVE}>35%</option>
                  <option value={TimingFactor.FORTY}>40%</option>
                  <option value={TimingFactor.FORTY_FIVE}>45%</option>
                  <option value={TimingFactor.FIFTY}>50%</option>
                </select>
                <IconChevronDown className={styles.selectIcon} size={20} />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="method">{t('projectSettings.fields.method')}</label>
              <div className={styles.selectWrapper}>
                <select
                  id="method"
                  value={formData.method}
                  onChange={(e) => handleInputChange('method', e.target.value)}
                  className={styles.select}
                >
                  <option value="">{t('projectSettings.fields.methodPlaceholder')}</option>
                  <option value={Methods.AGILE}>Agile</option>
                  <option value={Methods.CLASSIC}>Classic</option>
                  <option value={Methods.HYBRID}>Hybrid</option>
                </select>
                <IconChevronDown className={styles.selectIcon} size={20} />
              </div>
            </div>

            {(formData.method === Methods.AGILE || formData.method === Methods.HYBRID) && (
              <>
                <div className={styles.formGroup}>
                  <label htmlFor="sprintFrequency">{t('projectSettings.fields.sprintDuration')}</label>
                  <div className={styles.selectWrapper}>
                    <select
                      id="sprintFrequency"
                      value={formData.sprintFrequency}
                      onChange={(e) => handleInputChange('sprintFrequency', e.target.value)}
                      className={styles.select}
                    >
                      <option value="">{t('projectSettings.fields.sprintDurationPlaceholder')}</option>
                      <option value={SprintFrequency.WEEKLY_1}>1 Week</option>
                      <option value={SprintFrequency.WEEKLY_2}>2 Weeks</option>
                      <option value={SprintFrequency.WEEKLY_3}>3 Weeks</option>
                      <option value={SprintFrequency.MONTHLY}>1 Month</option>
                      <option value={SprintFrequency.WEEKLY_5}>5 Weeks</option>
                    </select>
                    <IconChevronDown className={styles.selectIcon} size={20} />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="firstSprintStartDate">{t('projectSettings.fields.firstSprintDate')}</label>
                  <input
                    id="firstSprintStartDate"
                    type="date"
                    value={formData.firstSprintStartDate}
                    onChange={(e) => handleInputChange('firstSprintStartDate', e.target.value)}
                    min={formData.startTime || undefined}
                    max={formData.endTime || undefined}
                  />
                  <small>{t('projectSettings.fields.firstSprintHelper')}</small>
                </div>
              </>
            )}

            <div className={styles.formGroup}>
              <label>{t('projectSettings.fields.icon')}</label>
              <div className={styles.iconUpload}>
                {previewUrl ? (
                  <div className={styles.iconPreview}>
                    <img src={previewUrl} alt="Project icon" />
                    <button
                      type="button"
                      onClick={handleRemoveIcon}
                      className={styles.removeIcon}
                      aria-label={t('projectSettings.fields.removeIcon')}
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                ) : (
                  <div className={styles.iconPlaceholder}>
                    <IconUpload size={32} />
                    <span>{t('projectSettings.fields.noIcon')}</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={styles.uploadButton}
                >
                  <IconUpload size={16} />
                  {previewUrl ? t('projectSettings.fields.changeIcon') : t('projectSettings.fields.uploadIcon')}
                </button>
              </div>
              <small>{t('projectSettings.fields.iconHelper')}</small>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2>{t('projectSettings.info')}</h2>
          
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>{t('projectSettings.fields.projectType')}</label>
              <p>{projectData.projectType || t('projectSettings.fields.na')}</p>
            </div>

            <div className={styles.infoItem}>
              <label>{t('projectSettings.fields.complexity')}</label>
              <p>{projectData.complexity || t('projectSettings.fields.na')}</p>
            </div>

            <div className={styles.infoItem}>
              <label>{t('projectSettings.fields.progress')}</label>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${projectData.progress || 0}%` }}
                />
                <span className={styles.progressText}>{projectData.progress || 0}%</span>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <ProjectMembers projectId={numericProjectId} />
        </section>
      </div>

      <div className={styles.footer}>
        <button
          onClick={handleCancel}
          className={styles.cancelButton}
          disabled={!hasChanges || updateProjectMutation.isPending}
        >
          {t('projectSettings.actions.cancel')}
        </button>
        <button
          onClick={handleSaveClick}
          className={styles.saveButton}
          disabled={!hasChanges || updateProjectMutation.isPending}
        >
          {updateProjectMutation.isPending ? t('projectSettings.actions.saving') : t('projectSettings.actions.save')}
        </button>
      </div>

      {showConfirmModal && (
        <div 
          className={`${styles.modalOverlay} ${isProcessing ? styles.processing : ''}`}
          onClick={() => !isProcessing && setShowConfirmModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <IconAlertTriangle size={48} color={isProcessing ? "#2D78DB" : "#ff9800"} />
              <h2>{isProcessing ? t('projectSettings.confirm.updating') : t('projectSettings.confirm.title')}</h2>
              <p>
                {isProcessing
                  ? t('projectSettings.confirm.processingMsg')
                  : t('projectSettings.confirm.areYouSure')
                }
              </p>
              {!isProcessing && (
                <div className={styles.warningBox}>
                  <p className={styles.warningText}>
                    <strong>{t('projectSettings.confirm.warningTitle')}</strong> {t('projectSettings.confirm.warningText')}
                  </p>
                  <p className={styles.warningSubtext}>
                    {t('projectSettings.confirm.irreversible')}
                  </p>
                </div>
              )}
            </div>

            <div className={styles.modalBody}>
              <h3>{t('projectSettings.confirm.changesToBeMade')}</h3>
              <div className={styles.changesList}>
                {getChangesList().map((change, index) => (
                  <div key={index} className={styles.changeItem}>
                    <span className={styles.changeLabel}>{change.label}:</span>
                    <div className={styles.changeValues}>
                      <span className={styles.oldValue}>{change.oldValue}</span>
                      <span className={styles.arrow}>→</span>
                      <span className={styles.newValue}>{change.newValue}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.modalFooter}>
              {isProcessing ? (
                <div className={styles.processingState}>
                  <div className={styles.spinner} />
                  <span>{t('projectSettings.confirm.processing')}</span>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className={styles.modalCancelButton}
                  >
                    {t('projectSettings.actions.cancel')}
                  </button>
                  <button
                    onClick={handleConfirmSave}
                    className={styles.modalConfirmButton}
                  >
                    {t('projectSettings.actions.confirm')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSettingsPage;
